import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { passwordResetAPI } from '../utils/api';

// ─── Component ────────────────────────────────────────────────────────────────
export default function PasswordReset() {
  const { t } = useTranslation();
  
  const USER_TYPES = [
    {
      type: 'tutor',
      icon: '👨🏫',
      label: t('passwordReset.tutorTitle'),
      desc: t('passwordReset.tutorDesc'),
      color: '#7b1d3a',
    },
    {
      type: 'student',
      icon: '👨🎓',
      label: t('passwordReset.studentTitle'),
      desc: t('passwordReset.studentDesc'),
      color: '#2b5fc0',
    },
    {
      type: 'parent',
      icon: '👨‍👩‍👧',
      label: t('passwordReset.parentTitle'),
      desc: t('passwordReset.parentDesc'),
      color: '#2d7a52',
    },
  ];

  const [activeType, setActiveType]       = useState(null);   // selected user type
  const [query, setQuery]                 = useState('');
  const [results, setResults]             = useState([]);
  const [searching, setSearching]         = useState(false);
  const [searchError, setSearchError]     = useState('');

  // Confirmation modal
  const [confirmUser, setConfirmUser]     = useState(null);
  const [resetting, setResetting]         = useState(false);

  // Success modal
  const [successData, setSuccessData]     = useState(null);  // { tempPassword, user }
  const [copied, setCopied]               = useState(false);

  // Toast
  const [toast, setToast]                 = useState({ show: false, message: '', type: '' });
  const debounceRef                       = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 4000);
  }, []);

  // ── Debounced Search ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeType) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim() || query.trim().length < 2) {
      setResults([]);
      setSearchError('');
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      setSearchError('');
      try {
        const res = await passwordResetAPI.search(activeType, query.trim());
        setResults(res.data.users || []);
        if ((res.data.users || []).length === 0) {
          setSearchError(t('passwordReset.noResults', { type: activeType, query }));
        }
      } catch (err) {
        setSearchError(t('passwordReset.searchFailed'));
        console.error(err);
      } finally {
        setSearching(false);
      }
    }, 450);
  }, [query, activeType, t]);

  // ── When user type changes, clear search ───────────────────────────────────
  const handleTypeSelect = (type) => {
    setActiveType(type);
    setQuery('');
    setResults([]);
    setSearchError('');
    setConfirmUser(null);
  };

  const handleBack = () => {
    setActiveType(null);
    setQuery('');
    setResults([]);
    setConfirmUser(null);
  };

  // ── Confirm Reset ──────────────────────────────────────────────────────────
  const handleResetConfirm = async () => {
    if (!confirmUser) return;
    setResetting(true);
    try {
      const res = await passwordResetAPI.reset(confirmUser.userType, confirmUser._id);
      setConfirmUser(null);
      setSuccessData({
        tempPassword: res.data.tempPassword,
        user: res.data.user,
      });
      // Remove this user from the results list to prevent double-reset
      setResults(prev => prev.filter(u => u._id !== confirmUser._id));
      showToast('✓', 'success'); // The checkmark is enough for toast as the big success modal pops up
    } catch (err) {
      const msg = err.response?.data?.message || t('passwordReset.resetFailed');
      showToast(msg, 'error');
      setConfirmUser(null);
    } finally {
      setResetting(false);
    }
  };

  // ── Copy to Clipboard ─────────────────────────────────────────────────────
  const handleCopy = () => {
    if (successData?.tempPassword) {
      navigator.clipboard.writeText(successData.tempPassword).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      });
    }
  };

  // ── Status badge ─────────────────────────────────────────────────────────
  const StatusBadge = ({ status, mustChangePassword }) => {
    if (mustChangePassword) return (
      <span style={{ background: '#fff3cd', color: '#856404', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, border: '1px solid #ffc107' }}>
        ⚠ {t('passwordReset.statusTemp')}
      </span>
    );
    if (status === 'active') return (
      <span style={{ background: '#d4edda', color: '#155724', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, border: '1px solid #c3e6cb' }}>
        ● {t('passwordReset.statusActive')}
      </span>
    );
    return (
      <span style={{ background: '#f8d7da', color: '#721c24', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, border: '1px solid #f5c6cb' }}>
        ○ {t('passwordReset.statusInactive')}
      </span>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout title={t('passwordReset.pageTitle')}>
      {/* Toast */}
      {toast.show && (
        <div style={{
          position: 'fixed', top: 80, right: 24, zIndex: 9999,
          background: toast.type === 'error' ? 'var(--red)' : 'var(--green)',
          color: '#fff', padding: '12px 20px', borderRadius: 10,
          fontWeight: 600, fontSize: 14, boxShadow: '0 4px 20px rgba(0,0,0,.25)',
          animation: 'fadeIn .3s ease',
        }}>
          {toast.type === 'error' ? '⚠ ' : '✓ '}{toast.message}
        </div>
      )}

      {/* Page Header */}
      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">
            {activeType
              ? <><span style={{ cursor: 'pointer', color: 'var(--wine)', marginRight: 8 }} onClick={handleBack}>← </span>{USER_TYPES.find(u => u.type === activeType)?.icon} {USER_TYPES.find(u => u.type === activeType)?.label} {t('passwordReset.pageTitle')}</>
              : `🔑 ${t('passwordReset.pageTitle')}`
            }
          </h1>
          <p className="text-sm text-muted">
            {activeType
              ? t('passwordReset.searchSubtitle', { type: USER_TYPES.find(u => u.type === activeType)?.label })
              : t('passwordReset.pageSubtitle')
            }
          </p>
        </div>
        {activeType && (
          <button className="btn btn-outline" onClick={handleBack}>
            {t('passwordReset.backBtn')}
          </button>
        )}
      </div>

      {/* ── CARD SELECTION VIEW ── */}
      {!activeType && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, marginTop: 8 }}>
          {USER_TYPES.map(u => (
            <button
              key={u.type}
              onClick={() => handleTypeSelect(u.type)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                gap: 12, padding: '28px 24px', borderRadius: 16,
                background: '#fff', border: `2px solid var(--border)`,
                cursor: 'pointer', textAlign: 'left', transition: 'all .2s ease',
                boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = u.color;
                e.currentTarget.style.boxShadow = `0 8px 30px rgba(0,0,0,.12)`;
                e.currentTarget.style.transform = 'translateY(-3px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,.06)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ fontSize: 48, lineHeight: 1 }}>{u.icon}</div>
              <div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>
                  {u.label}
                </div>
                <div style={{ fontSize: 13, color: 'var(--mid-grey)', lineHeight: 1.5 }}>{u.desc}</div>
              </div>
              <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 6, color: u.color, fontWeight: 600, fontSize: 13 }}>
                {t('passwordReset.managePwd')} <span style={{ fontSize: 16 }}>→</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* ── USER SEARCH VIEW ── */}
      {activeType && (
        <div style={{ marginTop: 8 }}>
          {/* Search Input */}
          <div style={{ position: 'relative', maxWidth: 520, marginBottom: 24 }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--mid-grey)', fontSize: 18, pointerEvents: 'none' }}>🔍</span>
            <input
              type="text"
              className="form-input-style"
              placeholder={t('passwordReset.searchPlaceholder')}
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
              style={{ paddingLeft: 44, height: 48, fontSize: 15, borderRadius: 10 }}
            />
            {searching && (
              <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--wine)' }}>
                <div style={{ width: 18, height: 18, border: '2px solid var(--wine)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite', display: 'inline-block' }} />
              </span>
            )}
          </div>

          {/* Tip */}
          {!query && (
            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--mid-grey)', fontSize: 14 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
              {t('passwordReset.searchTip')}
            </div>
          )}

          {/* Error / Empty */}
          {searchError && !searching && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--mid-grey)', fontSize: 14 }}>
              {searchError}
            </div>
          )}

          {/* Results Table */}
          {results.length > 0 && (
            <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid var(--border)', background: '#fff' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: 'var(--off-white)', borderBottom: '1px solid var(--border)' }}>
                    {[t('passwordReset.colName'), t('passwordReset.colEmail'), t('passwordReset.colPhone'), t('passwordReset.colStatus'), t('passwordReset.colLastReset'), t('passwordReset.colAction')].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--dark-grey)', letterSpacing: '.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.map((user, idx) => (
                    <tr
                      key={user._id}
                      style={{ borderBottom: idx < results.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background .15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--wine-pale)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '14px 16px', fontWeight: 600, color: 'var(--ink)' }}>
                        {user.name}
                        {user.studentId && <span style={{ display: 'block', fontSize: 11, color: 'var(--mid-grey)', fontWeight: 400, marginTop: 2 }}>{user.studentId}</span>}
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--dark-grey)' }}>{user.email || '—'}</td>
                      <td style={{ padding: '14px 16px', color: 'var(--dark-grey)' }}>{user.mobile || '—'}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <StatusBadge status={user.status} mustChangePassword={user.mustChangePassword} />
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--mid-grey)', fontSize: 12 }}>
                        {user.passwordResetAt ? new Date(user.passwordResetAt).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => setConfirmUser(user)}
                          disabled={user.status === 'inactive'}
                          style={{ background: 'var(--wine)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', cursor: user.status === 'inactive' ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13, opacity: user.status === 'inactive' ? .5 : 1 }}
                        >
                          🔑 {t('passwordReset.resetBtn')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── CONFIRMATION MODAL ── */}
      {confirmUser && (
        <div className="modal-overlay" onClick={() => !resetting && setConfirmUser(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>⚠ {t('passwordReset.confirmTitle')}</h3>
              <button className="close-btn" onClick={() => !resetting && setConfirmUser(null)} disabled={resetting}>×</button>
            </div>
            <div className="modal-body">
              {/* User Summary */}
              <div style={{ background: 'var(--off-white)', borderRadius: 10, padding: '16px 20px', marginBottom: 20, border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
                  <div><span style={{ color: 'var(--mid-grey)', minWidth: 90, display: 'inline-block' }}>{t('passwordReset.confirmName')}</span> <strong>{confirmUser.name}</strong></div>
                  <div><span style={{ color: 'var(--mid-grey)', minWidth: 90, display: 'inline-block' }}>{t('passwordReset.confirmEmail')}</span> {confirmUser.email || '—'}</div>
                  <div><span style={{ color: 'var(--mid-grey)', minWidth: 90, display: 'inline-block' }}>{t('passwordReset.confirmPhone')}</span> {confirmUser.mobile || '—'}</div>
                  <div><span style={{ color: 'var(--mid-grey)', minWidth: 90, display: 'inline-block' }}>{t('passwordReset.confirmType')}</span>
                    <span style={{ textTransform: 'capitalize', fontWeight: 600, color: 'var(--wine)' }}>
                      {confirmUser.userType === 'tutor' ? t('passwordReset.tutorTitle') : confirmUser.userType === 'student' ? t('passwordReset.studentTitle') : t('passwordReset.parentTitle')}
                    </span>
                  </div>
                </div>
              </div>
              {/* Warning */}
              <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 10, padding: '14px 16px', fontSize: 13, color: '#5d4037', lineHeight: 1.6 }}>
                ⚠ <strong>{t('passwordReset.confirmWarning').split('.')[0]}.</strong> {t('passwordReset.confirmWarning').substring(t('passwordReset.confirmWarning').indexOf('.') + 1)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-outline" onClick={() => setConfirmUser(null)} disabled={resetting}>{t('passwordReset.cancelBtn')}</button>
              <button
                className="btn btn-primary"
                disabled={resetting}
                onClick={handleResetConfirm}
                style={{ background: 'var(--wine)', minWidth: 140 }}
              >
                {resetting
                  ? <><span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite', marginRight: 8, verticalAlign: 'middle' }} />{t('passwordReset.resetting')}</>
                  : `✓ ${t('passwordReset.confirmBtn')}`
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SUCCESS MODAL ── */}
      {successData && (
        <div className="modal-overlay" onClick={() => { setSuccessData(null); setCopied(false); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header" style={{ borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ color: 'var(--green)' }}>✓ {t('passwordReset.successTitle')}</h3>
              <button className="close-btn" onClick={() => { setSuccessData(null); setCopied(false); }}>×</button>
            </div>
            <div className="modal-body">
              {/* User Info */}
              <div style={{ marginBottom: 20, fontSize: 14 }}>
                <div><span style={{ color: 'var(--mid-grey)' }}>{t('passwordReset.successUser')} </span><strong>{successData.user.name}</strong></div>
                <div style={{ marginTop: 4 }}><span style={{ color: 'var(--mid-grey)' }}>{t('passwordReset.successType')} </span>
                  <span style={{ textTransform: 'capitalize', fontWeight: 600, color: 'var(--wine)' }}>
                    {successData.user.userType === 'tutor' ? t('passwordReset.tutorTitle') : successData.user.userType === 'student' ? t('passwordReset.studentTitle') : t('passwordReset.parentTitle')}
                  </span>
                </div>
              </div>

              {/* Temp Password Display */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--mid-grey)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' }}>{t('passwordReset.successTempLabel')}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    flex: 1, background: 'var(--black)', color: '#4ade80', fontFamily: 'monospace',
                    fontSize: 22, fontWeight: 700, letterSpacing: 3, padding: '14px 20px',
                    borderRadius: 10, userSelect: 'all',
                  }}>
                    {successData.tempPassword}
                  </div>
                  <button
                    onClick={handleCopy}
                    style={{
                      background: copied ? 'var(--green)' : 'var(--wine)',
                      color: '#fff', border: 'none', borderRadius: 10,
                      padding: '14px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                      transition: 'background .2s', whiteSpace: 'nowrap',
                    }}
                  >
                    {copied ? `✓ ${t('passwordReset.copiedBtn')}` : `📋 ${t('passwordReset.copyBtn')}`}
                  </button>
                </div>
              </div>

              {/* Warning */}
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 10, padding: '12px 16px', fontSize: 12, color: '#856404', lineHeight: 1.6 }}>
                ⚠ <strong>{t('passwordReset.successWarning').split('.')[0]}.</strong><br />
                {t('passwordReset.successWarning').substring(t('passwordReset.successWarning').indexOf('.') + 1)}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-primary" style={{ background: 'var(--wine)' }} onClick={() => { setSuccessData(null); setCopied(false); }}>
                {t('passwordReset.doneBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
